import { Radio, Stack } from '@mantine/core'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { FunctionComponent } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Sizes } from '~/assets/style/tokens'
import { MethodCard } from '~/components/MethodCard'
import { Select } from '~/components/Select'
import { CreateSeatFormModel } from './CreateSeatStepper'
import { SeatSetupMethod, SeatSetupOption } from './seatSetupOption'

const BIDDER_SELECT_TRACKING_LABEL = 'pbs-create-seat-stepper-bidder-select'

type Props = {
  /** Empty until a bidder is picked: two of the four options depend on the bidder's capabilities. */
  options: SeatSetupOption[]
  /** Only passed when the flyout was opened without a preselected bidder. */
  catalogBidders?: BidderCatalog[]
  onBidderChange: (bidder: BidderCatalog) => void
  onSetupMethodChange: (method: SeatSetupMethod) => void
}

export const StepSetupMethod: FunctionComponent<Props> = ({ options, catalogBidders, onBidderChange, onSetupMethodChange }) => {
  const { control } = useFormContext<CreateSeatFormModel>()
  const setupMethod = useWatch<CreateSeatFormModel, 'setupMethod'>({ name: 'setupMethod' })
  const hasBidder = !!useWatch<CreateSeatFormModel, 'catalogBidderDisplayName'>({ name: 'catalogBidderDisplayName' })

  return (
    <Stack gap={Sizes[16]}>
      {catalogBidders && (
        <Select
          rules={{ required: true }}
          control={control}
          name="catalogBidderDisplayName"
          label="Integration"
          options={catalogBidders.map((bidder) => ({ label: bidder.displayName, value: bidder.displayName, trackingLabel: `${BIDDER_SELECT_TRACKING_LABEL}-${bidder.code}-apply` }))}
          searchable={true}
          onChange={(option) => {
            const bidder = catalogBidders.find((e) => e.displayName === option.label)
            if (bidder) onBidderChange(bidder)
          }}
        />
      )}
      {hasBidder && options.length === 0 && <div className="font-roboto text-base leading-[1.625rem] text-hurricane">This integration cannot be set up as a seat.</div>}
      {options.length > 0 && (
        <>
          <div className="font-roboto text-base leading-[1.625rem] font-bold text-ink">Setup method</div>
          <Radio.Group value={setupMethod} onChange={(value) => onSetupMethodChange(value as SeatSetupMethod)}>
            <Stack gap={Sizes[10]}>
              {options.map((option) => (
                <MethodCard key={option.value} {...option} />
              ))}
            </Stack>
          </Radio.Group>
        </>
      )}
    </Stack>
  )
}
